import React from "react";
import { ArrowUpRight, MapPin, Clock, ShieldCheck, PhoneCall } from "lucide-react";
import { HERO, PILLARS, IMAGES } from "../mock";
import Countdown from "./Countdown";

export default function Hero({ onOpenBooking }) {
  return (
    <>
      <section className="relative pt-28 lg:pt-32 pb-10 lg:pb-14 overflow-hidden min-h-[100vh] flex flex-col">
        {/* Full-bleed background image - grayscale, immersive */}
        <div className="absolute inset-0 pointer-events-none">
          <img
            src={IMAGES.heroBanner}
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover grayscale"
            style={{ opacity: 0.55 }}
          />
          {/* Layered dark gradients for depth + readability */}
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/85 via-[#0a0a0a]/55 to-[#0a0a0a]" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a]/70 via-transparent to-[#0a0a0a]/40" />
        </div>

        {/* Subtle blue glow */}
        <div className="absolute inset-0 gradient-glow pointer-events-none opacity-40" />

        <div className="container-max relative z-10 flex-1 flex flex-col justify-center">
          {/* Countdown bar */}
          <div className="flex flex-wrap items-center gap-3 mb-5 lg:mb-6 fade-in-up">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-black/50 backdrop-blur-md px-3 py-1.5 text-[11px] tracking-widest uppercase text-white/80">
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#3b5bff" }} />
              Early Bird — Promo di lancio
            </span>
            <Countdown />
          </div>

          {/* Eyebrow */}
          <div className="mb-4 fade-in-up">
            <span className="section-label">{HERO.eyebrow}</span>
          </div>

          {/* Title - even smaller to fit CTAs in fold */}
          <h1 className="font-display text-[28px] sm:text-[38px] lg:text-[52px] xl:text-[60px] leading-[1.05] font-bold text-white max-w-5xl fade-in-up drop-shadow-[0_2px_20px_rgba(0,0,0,0.6)]">
            {HERO.title_line1}{" "}
            <span className="italic font-light text-white/85">{HERO.title_line2}</span>{" "}
            <span className="text-white/60">{HERO.title_line3}</span>
          </h1>

          {/* Subtitle - shorter */}
          <p className="mt-4 text-white/80 text-sm lg:text-base max-w-2xl leading-relaxed fade-in-up">
            {HERO.subtitle}
          </p>

          {/* CTAs */}
          <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-3 fade-in-up">
            <a href="#prezzo" className="btn-primary">
              {HERO.ctaPrimary} <ArrowUpRight size={18} />
            </a>
            <button
              type="button"
              onClick={onOpenBooking}
              className="inline-flex items-center gap-3 bg-black/50 backdrop-blur-sm border border-white/20 hover:border-white/50 rounded-full py-3 px-5 text-white transition-colors"
            >
              <PhoneCall size={16} />
              <span className="flex flex-col items-start leading-tight">
                <span className="text-sm font-medium">Parla con un ns consulente</span>
                <span className="text-[10px] text-white/50 tracking-wide uppercase">
                  call gratuita e non impegnativa
                </span>
              </span>
            </button>
          </div>
          <p className="mt-3 text-white/60 text-xs lg:text-sm">{HERO.paymentNote}</p>

          {/* Quick meta pills */}
          <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-white/80 text-xs lg:text-sm fade-in-up">
            <span className="inline-flex items-center gap-2">
              <MapPin size={14} className="text-white/60" /> Pisa — Montacchiello Campus
            </span>
            <span className="text-white/20">|</span>
            <span className="inline-flex items-center gap-2">
              <Clock size={14} className="text-white/60" /> 4 giornate • 35 ore
            </span>
            <span className="text-white/20">|</span>
            <span className="inline-flex items-center gap-2">
              <ShieldCheck size={14} className="text-white/60" /> Garanzia zero rischi
            </span>
          </div>
        </div>
      </section>

      {/* Pillars strip - separate section with breathing room from hero */}
      <section className="relative py-16 lg:py-24 border-t border-white/5">
        <div className="container-max">
          <div className="mb-8 lg:mb-10 flex items-end justify-between flex-wrap gap-4">
            <span className="section-label">Cosa include il percorso</span>
            <span className="font-mono-label text-white/40 text-[11px]">04 elementi chiave</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
            {PILLARS.map((p, i) => (
              <div
                key={i}
                className="bg-card border border-white/10 rounded-2xl p-6 lg:p-7 hover-lift"
              >
                <div className="font-mono-label text-[10px] text-white/40 mb-3">0{i + 1}</div>
                <div className="font-display text-xl lg:text-2xl text-white mb-2 leading-tight">
                  {p.title}
                </div>
                <div className="text-white/60 text-sm leading-relaxed">{p.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
